import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import type { FirebaseApp } from 'firebase/app';
import type { Messaging, MessagePayload } from 'firebase/messaging';
import { BehaviorSubject, firstValueFrom, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiEndpointsService } from './api-endpoints.service';
import { AceNotificationService } from './ace-notification.service';
import { ApiHttpService } from './api-http.service';

interface FirebaseMessagingConfig {
  enabled?: boolean;
  apiKey?: string;
  authDomain?: string;
  projectId?: string;
  storageBucket?: string;
  messagingSenderId?: string;
  appId?: string;
  vapidKey?: string;
}

export interface ForegroundPushNotification {
  title: string;
  body: string;
  data: Record<string, string>;
}

export type PushRegistrationStatus =
  | 'idle'
  | 'disabled'
  | 'unsupported'
  | 'permission-required'
  | 'permission-denied'
  | 'registered'
  | 'failed';

type CompleteFirebaseMessagingConfig = FirebaseMessagingConfig &
  Required<
    Pick<
      FirebaseMessagingConfig,
      | 'apiKey'
      | 'authDomain'
      | 'projectId'
      | 'storageBucket'
      | 'messagingSenderId'
      | 'appId'
    >
  >;

declare global {
  interface Window {
    __ACE_FIREBASE_CONFIG__?: FirebaseMessagingConfig;
  }
}

@Injectable({ providedIn: 'root' })
export class WebPushNotificationService implements OnDestroy {
  private static readonly firebaseAppName = 'ace-gp-portal';
  private static readonly deviceIdStorageKey = 'ace-gp-portal-push-device-id';

  private initialization: Promise<void> | null = null;
  private messaging: Messaging | null = null;
  private messagingApi: typeof import('firebase/messaging') | null = null;
  private firebaseConfig: FirebaseMessagingConfig | null = null;
  private unsubscribeFromMessages: (() => void) | null = null;
  private registration: Promise<boolean> | null = null;
  private readonly foregroundNotificationSubject =
    new Subject<ForegroundPushNotification>();
  private readonly registrationStatusSubject =
    new BehaviorSubject<PushRegistrationStatus>('idle');

  readonly foregroundNotification$ =
    this.foregroundNotificationSubject.asObservable();
  readonly registrationStatus$ = this.registrationStatusSubject.asObservable();

  constructor(
    private readonly apiHttp: ApiHttpService,
    private readonly endpoints: ApiEndpointsService,
    private readonly notifications: AceNotificationService,
    private readonly router: Router
  ) {}

  start(): void {
    if (!this.isEnabled()) {
      this.registrationStatusSubject.next('disabled');
      return;
    }
    if (!this.initialization) {
      this.initialization = this.initialize().catch(error => {
        this.registrationStatusSubject.next('failed');
        console.warn('Web push initialization failed', error);
      });
    }
  }

  enable(requestPermission = true): void {
    if (!this.isEnabled()) {
      this.registrationStatusSubject.next('disabled');
      return;
    }
    if (!environment.teamApiUrl?.trim()) {
      this.registrationStatusSubject.next('unsupported');
      return;
    }
    this.start();
    void this.initialization
      ?.then(() => {
        if (!this.registration) {
          const attempt = this.requestPermissionAndRegister(requestPermission);
          this.registration = attempt;
          void attempt.then(registered => {
            if (!registered && this.registration === attempt) {
              this.registration = null;
            }
          });
        }
        return this.registration;
      })
      .catch(error => {
        this.registration = null;
        this.registrationStatusSubject.next('failed');
        console.warn('Web push registration failed', error);
      });
  }

  ngOnDestroy(): void {
    this.unsubscribeFromMessages?.();
    this.unsubscribeFromMessages = null;
    this.foregroundNotificationSubject.complete();
    this.registrationStatusSubject.complete();
  }

  private async initialize(): Promise<void> {
    if (!this.isEnabled()) {
      this.registrationStatusSubject.next('disabled');
      return;
    }
    if (!this.isBrowser() || !environment.teamApiUrl?.trim()) {
      this.registrationStatusSubject.next('unsupported');
      return;
    }

    const config = window.__ACE_FIREBASE_CONFIG__;
    if (!config || !this.hasRequiredConfig(config)) {
      this.registrationStatusSubject.next('unsupported');
      return;
    }

    const firebaseApi = await import('firebase/app');
    const messagingApi = await import('firebase/messaging');
    const supported = await messagingApi.isSupported().catch(() => false);
    if (!supported) {
      this.registrationStatusSubject.next('unsupported');
      return;
    }

    this.firebaseConfig = config;
    const firebaseOptions = {
      apiKey: config.apiKey,
      authDomain: config.authDomain,
      projectId: config.projectId,
      storageBucket: config.storageBucket,
      messagingSenderId: config.messagingSenderId,
      appId: config.appId,
    };
    const existingApp = firebaseApi
      .getApps()
      .find(app => app.name === WebPushNotificationService.firebaseAppName);
    const app: FirebaseApp =
      existingApp ??
      firebaseApi.initializeApp(
        firebaseOptions,
        WebPushNotificationService.firebaseAppName
      );
    this.messagingApi = messagingApi;
    this.messaging = messagingApi.getMessaging(app);
    this.unsubscribeFromMessages = messagingApi.onMessage(
      this.messaging,
      payload => {
        this.handleForegroundMessage(payload);
      }
    );
  }

  private async requestPermissionAndRegister(
    requestPermission: boolean
  ): Promise<boolean> {
    if (!this.messaging || !this.firebaseConfig || !this.isBrowser()) {
      this.registrationStatusSubject.next('unsupported');
      return false;
    }
    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      this.registrationStatusSubject.next('unsupported');
      return false;
    }
    if (Notification.permission === 'denied') {
      this.registrationStatusSubject.next('permission-denied');
      return false;
    }

    const currentPermission = Notification.permission;
    if (currentPermission !== 'granted' && !requestPermission) {
      this.registrationStatusSubject.next('permission-required');
      return false;
    }
    let requestedPermission: NotificationPermission = currentPermission;
    if (currentPermission !== 'granted') {
      this.registrationStatusSubject.next('permission-required');
      requestedPermission = await Notification.requestPermission();
    }
    if (requestedPermission !== 'granted') {
      this.registrationStatusSubject.next(
        requestedPermission === 'denied'
          ? 'permission-denied'
          : 'permission-required'
      );
      return false;
    }

    const registration = await navigator.serviceWorker.register(
      '/firebase-messaging-sw.js'
    );
    const tokenOptions = {
      serviceWorkerRegistration: registration,
      ...(this.firebaseConfig.vapidKey
        ? { vapidKey: this.firebaseConfig.vapidKey }
        : {}),
    };
    const token = await this.messagingApi?.getToken(
      this.messaging,
      tokenOptions
    );
    if (!token) {
      throw new Error('Firebase did not return a registration token');
    }

    await firstValueFrom(
      this.apiHttp.post(
        this.endpoints.registerPushToken(),
        {
          token,
          platform: 'web',
          deviceId: this.getDeviceId(),
        },
        { headers: { 'Content-Type': 'application/json' } }
      )
    );
    this.registrationStatusSubject.next('registered');
    return true;
  }

  private handleForegroundMessage(payload: MessagePayload): void {
    void firstValueFrom(this.notifications.refresh()).catch(() => undefined);

    const data = Object.fromEntries(
      Object.entries(payload.data ?? {}).filter(
        (entry): entry is [string, string] => typeof entry[1] === 'string'
      )
    );
    const title =
      payload.notification?.title?.trim() ||
      data['title']?.trim() ||
      'New notification';
    const body =
      payload.notification?.body?.trim() || data['body']?.trim() || '';
    this.foregroundNotificationSubject.next({ title, body, data });

    if (
      !this.isBrowser() ||
      !('Notification' in window) ||
      Notification.permission !== 'granted'
    ) {
      return;
    }

    const browserNotification = new Notification(title, {
      body,
      data,
    });
    browserNotification.onclick = () => {
      window.focus();
      this.navigateFromPush(payload.data ?? {});
      browserNotification.close();
    };
  }

  private navigateFromPush(data: Record<string, string>): void {
    const candidate = [data['deepLink'], data['url'], data['route']].find(
      value => typeof value === 'string'
    );
    if (
      !candidate ||
      !candidate.startsWith('/') ||
      candidate.startsWith('//') ||
      candidate.includes('://')
    ) {
      return;
    }
    void this.router.navigateByUrl(candidate);
  }

  private getDeviceId(): string {
    const existing = localStorage.getItem(
      WebPushNotificationService.deviceIdStorageKey
    );
    if (existing) return existing;

    const generated =
      typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(
      WebPushNotificationService.deviceIdStorageKey,
      generated
    );
    return generated;
  }

  private hasRequiredConfig(
    config: FirebaseMessagingConfig
  ): config is CompleteFirebaseMessagingConfig {
    return (
      config.enabled === true &&
      Boolean(
        config.apiKey &&
          config.authDomain &&
          config.projectId &&
          config.messagingSenderId &&
          config.appId
      )
    );
  }

  private isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof navigator !== 'undefined';
  }

  private isEnabled(): boolean {
    return environment.webPushEnabled === true;
  }
}
